import {
  AfterViewInit, Component, ComponentFactory,
  ComponentFactoryResolver, ComponentRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild
} from '@angular/core';
import _ from 'lodash';
import { Subscription } from 'rxjs';
import { AutoScoreQuestion } from '../../job-service/service-models/AutoScoreQuestion';
import { CustomApplicantElement } from '../../job-service/service-models/CustomApplicantElement';
import { FileUploadQuestion } from '../../job-service/service-models/FileUploadQuestion';
import { Information } from '../../job-service/service-models/information';
import { MultipleChoiceAutoScoreQuestion } from '../../job-service/service-models/MultipleChoiceAutoScoreQuestion';
import { MultipleChoiceQuestion } from '../../job-service/service-models/MultipleChoiceQuestion';
import { PageBreak } from '../../job-service/service-models/page-break';
import { Question } from '../../job-service/service-models/Question';
import { TextQuestion } from '../../job-service/service-models/TextQuestion';
import { VideoModel } from '../../job-service/service-models/VideoModel';
import { VideoQuestion } from '../../job-service/service-models/VideoQuestion';
import { YesNoQuestion } from '../../job-service/service-models/YesNoQuestion';
import { QuestionHostDirective } from './question-host.directive';
import { AutoScoreComponent } from './questions/auto-score/auto-score.component';
import { FileUploadComponent } from './questions/file-upload/file-upload.component';
import { InformationComponent } from './questions/information/information.component';
import { MultipleChoiceAutoScoreComponent } from './questions/multiple-choice-auto-score/multiple-choice-auto-score.component';
import { MultipleChoiceComponent } from './questions/multiple-choice/multiple-choice.component';
import { TextComponent } from './questions/text/text.component';
import { VideoComponent } from './questions/video/video.component';
import { YesNoComponent } from './questions/yes-no/yes-no.component';
import { ShortTextQuestion } from '../../job-service/service-models/short-text-question';
import { ShortTextComponent } from './questions/short-text/short-text.component';

@Component({
  selector: 'app-custom-applicant-element-renderer',
  templateUrl: './custom-applicant-element-renderer.component.html',
  styleUrls: ['./custom-applicant-element-renderer.component.scss']
})
export class CustomApplicantElementRendererComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  private viewInitialized = false;
  @Input() model: CustomApplicantElement;
  @Input() jobId: number;
  @Output() applicantElementUpdated: EventEmitter<CustomApplicantElement> = new EventEmitter();
  @ViewChild(QuestionHostDirective) questionHost: QuestionHostDirective;
  introVideo: VideoModel;
  subscription: Subscription;

  constructor(private componentFactoryResolver: ComponentFactoryResolver) {
    this.subscription = new Subscription();
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    this.renderQuestions();
  }

  ngOnInit() {
    if (this.model.introVideoId) {
      this.introVideo = this.model.videos.find(x => x.id === this.model.introVideoId);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.viewInitialized) {
      this.renderQuestions();
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  updateApplicantElement(emittedQuestion: Question) {
    const question = _.find(this.model.questions,
      function(q) { return q.id === emittedQuestion.id; });
    
    question.answers = emittedQuestion.answers;
    question.file = emittedQuestion.file;

    if (question.type === 'File') {
      (<FileUploadQuestion>question).isValid = (<FileUploadQuestion>emittedQuestion).isValid;
    }

    if (question.type === 'Video') {
      (<VideoQuestion>question).isValid = (<VideoQuestion>emittedQuestion).isValid;
    }

    this.applicantElementUpdated.emit(this.model);
  }

  private deserializeQuestion(question: any): Question {
    if (question.type === 'Text') { return Object.assign(new TextQuestion(), question); }
    if (question.type === 'AutoScore') { return Object.assign(new AutoScoreQuestion(), question); }
    if (question.type === 'YesNo') { return Object.assign(new YesNoQuestion(), question); }
    if (question.type === 'File') { return Object.assign(new FileUploadQuestion(), question); }
    if (question.type === 'MultipleChoice') { return Object.assign(new MultipleChoiceQuestion(), question); }
    if (question.type === 'MultipleChoiceAutoScore') { return Object.assign(new MultipleChoiceAutoScoreQuestion(), question); }
    if (question.type === 'Video') { return Object.assign(new VideoQuestion(), question); }
    if (question.type === 'PageBreak') { return Object.assign(new PageBreak(), question); }
    if (question.type === 'Information') { return Object.assign(new Information(), question); }
    if (question.type === 'ShortText') { return Object.assign(new ShortTextQuestion(), question); }
  }

  private getComponentFactory(question: Question): ComponentFactory<any> {
    let type: any;

    if (question instanceof TextQuestion) { type = TextComponent; }
    if (question instanceof AutoScoreQuestion) { type = AutoScoreComponent; }
    if (question instanceof YesNoQuestion) { type = YesNoComponent; }
    if (question instanceof FileUploadQuestion) { type = FileUploadComponent; }
    if (question instanceof MultipleChoiceQuestion) { type = MultipleChoiceComponent; }
    if (question instanceof MultipleChoiceAutoScoreQuestion) { type = MultipleChoiceAutoScoreComponent; }
    if (question instanceof VideoQuestion) { type = VideoComponent; }
    if (question instanceof PageBreak) { /*??*/ }
    if (question instanceof Information) { type = InformationComponent; }
    if (question instanceof ShortTextQuestion) { type = ShortTextComponent; }

    return type !== undefined ? this.componentFactoryResolver.resolveComponentFactory(type) : undefined;
  }

  private renderQuestions(): any {
    this.questionHost.viewContainerRef.clear();
    this.model.questions.forEach(x => {
      const question: Question = this.deserializeQuestion(x);
      const componentFactory = this.getComponentFactory(question);
      if (componentFactory === undefined) { return; }
      const viewContainerRef = this.questionHost.viewContainerRef;
      const componentRef: ComponentRef<any> = viewContainerRef.createComponent(componentFactory);

      if (componentRef !== undefined) {
        this.subscription.add(componentRef.instance.event.subscribe(q => {
          this.updateApplicantElement(q);
        }));
      }

      componentRef.instance.question = question;
      componentRef.instance.jobId = this.jobId;
      componentRef.instance.element = this.model;
    });
  }

}
